import { Box } from "@mui/material";
import Link from "next/link";
import type { CSSProperties, ReactNode } from "react";
import {
  SITE_COLOR,
  SITE_EASE,
  SITE_MEDIA,
  SITE_VAR,
} from "src/domains/shared/constants/styles/site";
import { revealRef } from "./motion/reveal";
import { SiteTitle } from "./SiteText";

const LINK_CARD_STYLES = {
  root: {
    "&:hover [data-card-media] img": { transform: "scale(1.04)" },
  },
  media: {
    borderRadius: SITE_VAR.radiusImage,
    overflow: "hidden",
    background: SITE_COLOR.panel,
    aspectRatio: "3/2",
    position: "relative",
    "& img": {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      transition: `transform .7s ${SITE_EASE.out}`,
    },
  },
  kicker: {
    fontSize: "15px",
    fontWeight: 600,
    color: SITE_COLOR.ink3,
    margin: "26px 0 8px",
    lineHeight: 1.7,
    maxWidth: "32em",
    [SITE_MEDIA.phone]: { marginTop: "18px" },
  },
  title: {
    fontSize: "clamp(20px,1.8vw,24px)",
    lineHeight: 1.4,
    fontWeight: 700,
    letterSpacing: "-.02em",
    marginBottom: "10px",
  },
  text: {
    fontSize: "16px",
    lineHeight: 1.7,
    color: SITE_COLOR.ink2,
    maxWidth: "32em",
  },
} as const;

export interface LinkCardProps {
  /** 없으면 링크가 아닌 소개 카드(포인터를 올리면 그림만 살짝 커진다) */
  href?: string;
  /** 3:2 그림 칸에 들어갈 그림(`SiteImage` 또는 그림 파일 `<img>`) */
  media: ReactNode;
  kicker: ReactNode;
  title: ReactNode;
  text: ReactNode;
  /** 줄 안에서의 등장 순서(시안 `data-stagger`) */
  revealIndex?: number;
}

/** 그림 + 작은 머리말 + 제목 + 설명 카드(시안 `.pcard`). */
export function LinkCard({
  href,
  media,
  kicker,
  title,
  text,
  revealIndex = 0,
}: LinkCardProps) {
  const body = (
    <>
      <Box data-card-media="" sx={LINK_CARD_STYLES.media}>
        {media}
      </Box>
      <Box component="p" sx={LINK_CARD_STYLES.kicker}>
        {kicker}
      </Box>
      <Box component="h3" sx={LINK_CARD_STYLES.title}>
        {typeof title === "string" ? <SiteTitle text={title} /> : title}
      </Box>
      <Box component="p" sx={LINK_CARD_STYLES.text}>
        {text}
      </Box>
    </>
  );
  const revealStyle = { "--i": revealIndex } as CSSProperties;
  if (href) {
    return (
      <Box
        component={Link}
        href={href}
        ref={revealRef}
        data-reveal="up"
        style={revealStyle}
        sx={LINK_CARD_STYLES.root}
      >
        {body}
      </Box>
    );
  }
  return (
    <Box
      component="article"
      ref={revealRef}
      data-reveal="up"
      style={revealStyle}
      sx={LINK_CARD_STYLES.root}
    >
      {body}
    </Box>
  );
}
